import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  MapPin, 
  Filter, 
  RefreshCw,
  Search,
  ExternalLink,
  Navigation,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Calendar,
  DollarSign,
  User,
  Users,
  Briefcase,
  Eye
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { format, isToday, isThisWeek, differenceInDays } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { 
  DATE_FILTER_PRESETS, 
  filterJobsByDateRange,
  formatJobScheduledDate 
} from '../../services/dateRangeFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom colored markers
const createColoredIcon = (color) => {
  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
      <path fill="${color}" stroke="#333" stroke-width="1" d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 24 12 24s12-16.8 12-24C24 5.4 18.6 0 12 0z"/>
      <circle fill="white" cx="12" cy="12" r="5"/>
    </svg>
  `;
  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker',
    iconSize: [24, 36],
    iconAnchor: [12, 36],
    popupAnchor: [0, -36],
  });
};

const greenIcon = createColoredIcon('#22c55e');
const yellowIcon = createColoredIcon('#eab308');
const redIcon = createColoredIcon('#ef4444');
const blueIcon = createColoredIcon('#3b82f6');

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

// Get job status and color
const getJobStatus = (job) => {
  const stage = job.stage?.toLowerCase() || '';
  
  // Complete stages
  if (['complete', 'completed', 'paid', 'closed'].includes(stage)) {
    return { status: 'complete', color: 'green', icon: greenIcon, label: 'Complete' };
  }
  
  // In-progress / Scheduled stages
  if (['scheduled', 'production', 'installation', 'install', 'in_progress', 'qc', 'final_inspection'].includes(stage)) {
    return { status: 'in_progress', color: 'yellow', icon: yellowIcon, label: 'In Progress' };
  }
  
  // All others (needs attention)
  return { status: 'pending', color: 'red', icon: redIcon, label: 'Needs Attention' };
};

// Map recenter component
const MapRecenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

// Hover Tooltip Content - Quick summary on hover
const JobTooltipContent = ({ job }) => {
  const { status, label } = getJobStatus(job);
  const scheduledDate = job.scheduled_completion_date || job.scheduled_date;
  
  return (
    <div className="min-w-48 text-xs">
      <div className="font-bold text-slate-900 mb-1 truncate">{job.customer_name || 'Unknown'}</div>
      <div className="space-y-0.5 text-slate-600">
        {job.sales_rep_name && (
          <div className="flex items-center gap-1">
            <Briefcase className="h-2.5 w-2.5" />
            <span>Sales: {job.sales_rep_name}</span>
          </div>
        )}
        {job.crew_leader_name && (
          <div className="flex items-center gap-1">
            <Users className="h-2.5 w-2.5" />
            <span>Crew: {job.crew_leader_name}</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <DollarSign className="h-2.5 w-2.5" />
          <span>{formatCurrency(job.contract_amount || job.estimated_value || 0)}</span>
        </div>
        {scheduledDate && (
          <div className="flex items-center gap-1">
            <Calendar className="h-2.5 w-2.5" />
            <span>{format(new Date(scheduledDate), 'MMM d')}</span>
          </div>
        )}
      </div>
      <div className={`mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium inline-block ${
        status === 'complete' ? 'bg-green-100 text-green-700' :
        status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
        'bg-red-100 text-red-700'
      }`}>
        {label}
      </div>
      <div className="mt-1 text-[10px] text-blue-600 font-medium">Click for details →</div>
    </div>
  );
};

// Job Popup Content - Full details on click with "View Full Project" CTA
const JobPopupContent = ({ job }) => {
  const { status, label } = getJobStatus(job);
  const scheduledDate = job.scheduled_completion_date || job.scheduled_date;
  
  const openInGoogleMaps = () => {
    const address = encodeURIComponent(job.address || '');
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };
  
  const viewFullProject = (e, newTab = false) => {
    e?.stopPropagation();
    const url = `/workspace/records?source=sales&mode=projects&id=${job.id}`;
    if (newTab || e?.ctrlKey || e?.metaKey) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  };
  
  return (
    <div className="min-w-72 p-2">
      {/* Header with Status Badge */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
        <h3 className="font-bold text-slate-900 text-base">{job.customer_name || 'Unknown'}</h3>
        <Badge 
          variant="outline" 
          className={`text-xs ${
            status === 'complete' ? 'bg-green-100 text-green-700 border-green-300' :
            status === 'in_progress' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
            'bg-red-100 text-red-700 border-red-300'
          }`}
        >
          {label}
        </Badge>
      </div>
      
      {/* Job Details Grid */}
      <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 mb-3">
        {/* Sales Rep */}
        <div className="flex items-center gap-2">
          <Briefcase className="h-3.5 w-3.5 text-slate-400" />
          <div>
            <div className="text-[10px] text-slate-400 uppercase">Sales Rep</div>
            <div className="font-medium text-slate-700">{job.sales_rep_name || 'Unassigned'}</div>
          </div>
        </div>
        
        {/* Crew Leader */}
        <div className="flex items-center gap-2">
          <Users className="h-3.5 w-3.5 text-slate-400" />
          <div>
            <div className="text-[10px] text-slate-400 uppercase">Crew Leader</div>
            <div className="font-medium text-slate-700">{job.crew_leader_name || 'Unassigned'}</div>
          </div>
        </div>
        
        {/* Total Price */}
        <div className="flex items-center gap-2">
          <DollarSign className="h-3.5 w-3.5 text-slate-400" />
          <div>
            <div className="text-[10px] text-slate-400 uppercase">Total Price</div>
            <div className="font-medium text-slate-700">{formatCurrency(job.contract_amount || job.estimated_value || 0)}</div>
          </div>
        </div>
        
        {/* Remaining Balance */}
        <div className="flex items-center gap-2">
          <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
          <div>
            <div className="text-[10px] text-slate-400 uppercase">Remaining</div>
            <div className="font-medium text-emerald-600">{formatCurrency(job.remaining_balance || 0)}</div>
          </div>
        </div>
        
        {/* Scheduled Completion */}
        <div className="flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5 text-slate-400" />
          <div>
            <div className="text-[10px] text-slate-400 uppercase">Scheduled</div>
            <div className="font-medium text-slate-700">
              {scheduledDate ? format(new Date(scheduledDate), 'MMM d, yyyy') : 'Not scheduled'}
            </div>
          </div>
        </div>
        
        {/* Address */}
        <div className="flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5 text-slate-400" />
          <div>
            <div className="text-[10px] text-slate-400 uppercase">Location</div>
            <div className="font-medium text-slate-700 truncate max-w-24" title={job.address}>
              {job.address ? job.address.split(',')[0] : 'No address'}
            </div>
          </div>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex gap-2 pt-2 border-t border-slate-200">
        <Button 
          size="sm" 
          variant="outline"
          className="flex-1 text-xs h-8"
          onClick={openInGoogleMaps}
        >
          <Navigation className="h-3 w-3 mr-1" />
          Directions
        </Button>
        <Button 
          size="sm"
          className="flex-1 text-xs bg-blue-600 hover:bg-blue-700 h-8"
          onClick={(e) => viewFullProject(e)}
          onAuxClick={(e) => { if (e.button === 1) viewFullProject(e, true); }}
        >
          <Eye className="h-3 w-3 mr-1" />
          View Full Project
        </Button>
      </div>
      
      {/* Tip for new tab */}
      <div className="text-[10px] text-slate-400 text-center mt-1">
        Ctrl/Cmd+Click or middle-click to open in new tab
      </div>
    </div>
  );
};

// Stats Card
const StatsCard = ({ icon: Icon, label, value, color }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/40 border border-slate-700/50">
    <div className={`p-2 rounded-lg ${color}`}>
      <Icon className="h-4 w-4 text-white" />
    </div>
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="text-lg font-bold text-white">{value}</p>
    </div>
  </div>
);

export const ProjectMap = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [users, setUsers] = useState([]);
  // Shared date range filter state
  const [dateFilterPreset, setDateFilterPreset] = useState(DATE_FILTER_PRESETS.ALL);
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [statusFilter, setStatusFilter] = useState('all'); // all, complete, in_progress, pending
  const [searchQuery, setSearchQuery] = useState('');
  const [mapCenter, setMapCenter] = useState([27.9506, -82.4572]); // Tampa, FL default
  const [selectedJob, setSelectedJob] = useState(null);

  // Handle date filter change from shared component
  const handleDateFilterChange = (preset, customRange) => {
    setDateFilterPreset(preset);
    setCustomDateRange(customRange || { from: null, to: null });
  };

  // Geocode addresses to get coordinates
  const geocodeAddress = useCallback(async (address) => {
    if (!address) return null;
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
        };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    return null;
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [jobsRes, usersRes] = await Promise.all([
        axios.get(`${API}/jobs`, { headers }),
        axios.get(`${API}/users`, { headers }),
      ]);
      
      const jobsData = jobsRes.data || [];
      const usersData = usersRes.data || [];
      setUsers(usersData);
      
      // Filter to operations jobs only
      const opsStages = ['scheduled', 'prep', 'production', 'installation', 'install', 'qc', 'final_inspection', 'complete', 'completed', 'invoice', 'pending_payment', 'paid'];
      const opsJobs = jobsData.filter(job => opsStages.includes(job.stage?.toLowerCase()));
      
      // Enrich jobs with user names and geocode addresses
      const enrichedJobs = await Promise.all(
        opsJobs.map(async (job) => {
          const assignedUser = usersData.find(u => u.id === job.assigned_to);
          const salesRep = usersData.find(u => u.id === job.sales_rep_id || u.id === job.created_by);
          const crewLeader = usersData.find(u => u.id === job.crew_leader_id || u.id === job.production_manager_id);
          
          // Check if job already has coordinates
          let coords = job.coordinates;
          if (!coords && job.address) {
            // Geocode the address (with rate limiting)
            await new Promise(resolve => setTimeout(resolve, 100)); // Rate limit
            coords = await geocodeAddress(job.address);
          }
          
          return {
            ...job,
            assigned_to_name: assignedUser 
              ? `${assignedUser.first_name || ''} ${assignedUser.last_name || ''}`.trim() 
              : null,
            sales_rep_name: salesRep
              ? `${salesRep.first_name || ''} ${salesRep.last_name || ''}`.trim()
              : job.sales_rep_name || null,
            crew_leader_name: crewLeader
              ? `${crewLeader.first_name || ''} ${crewLeader.last_name || ''}`.trim()
              : job.crew_leader_name || job.assigned_to_name || null,
            coordinates: coords,
          };
        })
      );
      
      setJobs(enrichedJobs);
      
      // Set initial map center based on first job with coordinates
      const firstJobWithCoords = enrichedJobs.find(j => j.coordinates);
      if (firstJobWithCoords) {
        setMapCenter([firstJobWithCoords.coordinates.lat, firstJobWithCoords.coordinates.lng]);
      }
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load project map data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [geocodeAddress]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply filters using shared date range filter service
  useEffect(() => {
    // First apply date range filter
    let filtered = filterJobsByDateRange(jobs, dateFilterPreset, customDateRange);
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => {
        const { status } = getJobStatus(job);
        return status === statusFilter;
      });
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(job => 
        job.customer_name?.toLowerCase().includes(query) ||
        job.address?.toLowerCase().includes(query) ||
        job.assigned_to_name?.toLowerCase().includes(query)
      );
    }
    
    setFilteredJobs(filtered);
  }, [jobs, dateFilterPreset, customDateRange, statusFilter, searchQuery]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Calculate stats
  const stats = {
    total: filteredJobs.length,
    complete: filteredJobs.filter(j => getJobStatus(j).status === 'complete').length,
    inProgress: filteredJobs.filter(j => getJobStatus(j).status === 'in_progress').length,
    pending: filteredJobs.filter(j => getJobStatus(j).status === 'pending').length,
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-muted-foreground dark:text-slate-400">Loading project map...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground dark:text-white">Project Map</h1>
            <p className="text-muted-foreground dark:text-slate-400">
              Quality control visibility for active projects
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="dark:bg-slate-700 dark:border-slate-600"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Shared Date Range Filter */}
        <DateRangeFilter
          value={dateFilterPreset}
          customRange={customDateRange}
          onChange={handleDateFilterChange}
          showInstallWeeks={true}
          showCustomRange={true}
          showSaveView={true}
          pageName="project_map"
        />

        {/* Additional Filters */}
        <Card className="dark:bg-slate-800/60 dark:border-slate-700">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-200">Status & Search:</span>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48 dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="complete">Complete (Green)</SelectItem>
                  <SelectItem value="in_progress">In Progress (Yellow)</SelectItem>
                  <SelectItem value="pending">Needs Attention (Red)</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="relative flex-1 min-w-48">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search customer, address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard icon={MapPin} label="Total Projects" value={stats.total} color="bg-blue-600" />
          <StatsCard icon={CheckCircle} label="Complete" value={stats.complete} color="bg-green-600" />
          <StatsCard icon={Clock} label="In Progress" value={stats.inProgress} color="bg-yellow-600" />
          <StatsCard icon={AlertCircle} label="Needs Attention" value={stats.pending} color="bg-red-600" />
        </div>

        {/* Map */}
        <Card className="dark:bg-slate-800/60 dark:border-slate-700 overflow-hidden">
          <div className="h-[500px] relative">
            <MapContainer
              center={mapCenter}
              zoom={10}
              style={{ height: '100%', width: '100%' }}
              className="rounded-lg"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapRecenter center={mapCenter} />
              
              {filteredJobs.map((job) => {
                if (!job.coordinates) return null;
                const { icon } = getJobStatus(job);
                
                return (
                  <Marker
                    key={job.id}
                    position={[job.coordinates.lat, job.coordinates.lng]}
                    icon={icon}
                  >
                    {/* Hover Tooltip - Quick summary */}
                    <Tooltip 
                      direction="top" 
                      offset={[0, -30]} 
                      opacity={1}
                      className="custom-tooltip"
                    >
                      <JobTooltipContent job={job} />
                    </Tooltip>
                    {/* Click Popup - Full details with View Full Project CTA */}
                    <Popup>
                      <JobPopupContent job={job} />
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
            
            {/* Legend */}
            <div className="absolute bottom-4 left-4 z-[1000] bg-slate-900/90 backdrop-blur-sm p-3 rounded-lg border border-slate-700">
              <p className="text-xs font-medium text-slate-200 mb-2">Legend</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs text-slate-300">Complete</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span className="text-xs text-slate-300">In Progress / Scheduled</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-xs text-slate-300">Needs Attention</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Jobs Without Coordinates Warning */}
        {filteredJobs.filter(j => !j.coordinates).length > 0 && (
          <Card className="dark:bg-amber-900/20 border-amber-500/30">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-amber-400" />
                <div>
                  <p className="font-medium text-amber-200">
                    {filteredJobs.filter(j => !j.coordinates).length} job(s) could not be mapped
                  </p>
                  <p className="text-sm text-amber-300/70">
                    These jobs have addresses that could not be geocoded. Consider updating their addresses.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ProjectMap;
